// TripStep — Memoized 4-column timeline row for the chronological timeline
// redesign.
//
// Renders a single concrete journey action (e.g. "Leave home", "Airport
// parking", "Hotel check-in") as a 4-column row with vertical borders:
//
//   col 1: scheduled time (caption) + StatusIndicator
//   col 2: lucide icon + bold title + descriptive paragraph
//   col 3: KeyValuePairs (columns=1) of step detail pairs
//   col 4: contextual tip text
//
// Below 768px (`isMobile`) the 4-column grid collapses into a single stacked
// column with the vertical dividers removed so the layout stays readable on
// small screens.
//
// The component is wrapped in React.memo with a custom comparator keyed on the
// display-affecting props (`status`, `time`, `title`, `details`) so that a
// status change on one sibling step does not re-render unaffected steps
// (Req 12.2, 12.4).
//
// The `icon` prop is a lucide icon *key* (string) — it is mapped to a
// lucide-react component via `resolveIcon`, falling back to a neutral marker
// for unknown keys.
//
// Validates: Requirements 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 10.1, 10.2, 11.5, 12.2, 12.4

import React from 'react';
import {
  Home,
  Car,
  ParkingCircle,
  Shield,
  PlaneTakeoff,
  PlaneLanding,
  Plane,
  Building,
  BedDouble,
  Utensils,
  Calendar,
  Users,
  MapPin,
  Clock,
  Circle,
} from 'lucide-react';

import { colors, typography, fontWeights, spacing, radii } from '../../../styles/designTokens';
import { StatusIndicator } from './StatusIndicator';
import { KeyValuePairs } from './KeyValuePairs';

/**
 * @typedef {'completed'|'in-progress'|'upcoming'} StepStatus
 */

// Maps a lucide icon *key* (kebab-case name or step-type alias) to its
// lucide-react component. Both the design's icon-key vocabulary
// (e.g. "location-pin"→MapPin) and the TripStepData stepType values
// (e.g. "leave_home", "hotel_checkin") resolve here so callers can pass
// whichever they have without TripStep needing to know the difference.
const ICON_MAP = new Map([
  // step-type aliases
  ['leave_home', Home],
  ['parking', ParkingCircle],
  ['security', Shield],
  ['gate', PlaneTakeoff],
  ['flight_arrival', PlaneLanding],
  ['landing', PlaneLanding],
  ['ground_transport', Car],
  ['hotel_checkin', Building],
  ['hotel_checkout', Building],
  ['dining', Utensils],
  ['meeting', Calendar],
  ['arrive_home', Home],
  // kebab/lower-case lucide-style keys
  ['home', Home],
  ['car', Car],
  ['square-parking', ParkingCircle],
  ['parking-circle', ParkingCircle],
  ['shield', Shield],
  ['plane-takeoff', PlaneTakeoff],
  ['plane-landing', PlaneLanding],
  ['plane', Plane],
  ['building', Building],
  ['bed-double', BedDouble],
  ['hotel', BedDouble],
  ['utensils', Utensils],
  ['calendar', Calendar],
  ['users', Users],
  ['map-pin', MapPin],
  ['location-pin', MapPin],
  ['clock', Clock],
  ['circle', Circle],
]);

/**
 * Resolves a lucide icon key to its component, defaulting to a neutral marker
 * when the key is missing or unrecognized.
 *
 * @param {string} icon - lucide icon key
 * @returns {React.ComponentType} lucide icon component
 */
function resolveIcon(icon) {
  return (icon && ICON_MAP.get(icon)) || Circle;
}

// Human-readable status label used for the accessible name (mirrors the labels
// rendered by StatusIndicator).
const STATUS_LABELS = new Map([
  ['completed', 'Completed'],
  ['in-progress', 'In progress'],
  ['upcoming', 'Upcoming'],
]);

/**
 * @typedef {Object} TripStepProps
 * @property {string} time                                          Localized time label, e.g. "6:30 AM"
 * @property {StepStatus} status                                    Temporal status of the step
 * @property {string} icon                                          lucide icon key
 * @property {string} title                                         Step title (bold)
 * @property {string} description                                   Descriptive paragraph
 * @property {Array<{label:string,value:React.ReactNode}>} details  Key-value detail pairs
 * @property {string} tip                                           Contextual tip text
 * @property {boolean} [isMobile]                                   Collapses to single column when true
 */

/**
 * TripStep renders one journey action as a 4-column row (single stacked column
 * on mobile). Returns null for a falsy step input, mirroring EventRow.
 *
 * @param {TripStepProps} props
 * @returns {React.ReactElement|null}
 */
function TripStepInner({ time, status, icon, title, description, details, tip, isMobile = false }) {
  if (!title && !time) {
    return null;
  }

  const Icon = resolveIcon(icon);
  const statusLabel = STATUS_LABELS.get(status) || STATUS_LABELS.get('upcoming');

  // Each column past the first gets a left border divider on desktop; on mobile
  // the columns stack vertically and the dividers are dropped.
  const columnDivider = isMobile ? 'none' : `1px solid ${colors.divider}`;
  const columnPadding = isMobile ? '0' : `0 ${spacing.cardPadding}`;
  const firstColumnPadding = isMobile ? '0' : `0 ${spacing.cardPadding} 0 0`;

  return (
    <div
      data-testid="trip-step"
      data-step-status={status}
      aria-label={`${time}, ${title}, ${statusLabel}`}
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
        gap: isMobile ? spacing.elementGap : '0',
        alignItems: 'start',
        padding: spacing.cardPadding,
        backgroundColor: colors.surface,
        borderRadius: radii.inner,
      }}
    >
      {/* Column 1 — time + status */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.elementGap,
          padding: firstColumnPadding,
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontSize: typography.caption.fontSize,
            fontWeight: fontWeights.medium,
            color: colors.textSecondary,
          }}
        >
          {time}
        </span>
        <StatusIndicator status={status} />
      </div>

      {/* Column 2 — icon + title + description */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.elementGap,
          padding: columnPadding,
          borderLeft: columnDivider,
          minWidth: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Icon size={16} color={colors.textPrimary} aria-hidden="true" />
          <span
            style={{
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.bold,
              color: colors.textPrimary,
            }}
          >
            {title}
          </span>
        </div>
        {description ? (
          <p
            style={{
              margin: 0,
              fontSize: typography.body.fontSize,
              fontWeight: typography.body.fontWeight,
              color: colors.cardBodyText,
            }}
          >
            {description}
          </p>
        ) : null}
      </div>

      {/* Column 3 — detail pairs */}
      <div
        style={{
          padding: columnPadding,
          borderLeft: columnDivider,
          minWidth: 0,
        }}
      >
        <KeyValuePairs columns={1} items={details} />
      </div>

      {/* Column 4 — contextual tip */}
      <div
        style={{
          padding: columnPadding,
          borderLeft: columnDivider,
          minWidth: 0,
        }}
      >
        {tip ? (
          <span
            style={{
              fontSize: typography.caption.fontSize,
              fontWeight: typography.caption.fontWeight,
              color: colors.textSecondary,
            }}
          >
            {tip}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Custom memo comparator. Returns true (skip re-render) when the
 * display-affecting props are unchanged. `details` is compared by reference,
 * matching the EventRow memoization pattern and the design's contract
 * (Req 12.4) — the pure model layer produces a stable array reference when a
 * step's details do not change.
 *
 * @param {TripStepProps} prev
 * @param {TripStepProps} next
 * @returns {boolean}
 */
export function areTripStepPropsEqual(prev, next) {
  return (
    prev.status === next.status &&
    prev.time === next.time &&
    prev.title === next.title &&
    prev.details === next.details &&
    prev.icon === next.icon &&
    prev.description === next.description &&
    prev.tip === next.tip &&
    prev.isMobile === next.isMobile
  );
}

export const TripStep = React.memo(TripStepInner, areTripStepPropsEqual);

export default TripStep;
